import type { MetaFunction } from "@remix-run/cloudflare";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { DownloadButton } from "~/components/ui/download-button";
import { ProtectedDownloadLink } from "~/components/ui/protected-download-link";
import { 
  Download, 
  Eye, 
  Heart, 
  Calendar,
  User,
  Shield,
  Star,
  FileText,
  BarChart3,
  MessageSquare,
  Clock,
  CheckCircle,
  Palette,
  Image,
  Sword
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "EVE2Scarlet - Character Outfit Mod | stellarblademod.com" },
    { 
      name: "description", 
      content: "EVE2Scarlet mod for Stellar Blade - Transform EVE's appearance to match Scarlet with complete face, hair, body and weapon customization." 
    },
    { name: "keywords", content: "stellar blade eve2scarlet, stellar blade character mod, scarlet outfit, face hair body weapon mod, eve transformation" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/mods/eve2scarlet" },
    
    // Open Graph
    { property: "og:title", content: "EVE2Scarlet - Character Outfit Mod" },
    { property: "og:description", content: "Transform EVE's appearance to match Scarlet with complete customization package." },
    { property: "og:type", content: "article" },
    { property: "og:url", content: "https://stellarblademod.com/mods/eve2scarlet" },
    { property: "og:image", content: "https://stellarblademod.com/mods/eve2scarlet1.webp" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "EVE2Scarlet - Character Outfit Mod" },
    { name: "twitter:description", content: "Transform EVE's appearance to match Scarlet with complete customization package." },
    { name: "twitter:image", content: "https://stellarblademod.com/mods/eve2scarlet1.webp" },
  ];
};

export default function EVE2Scarlet() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div 
        className="relative text-white py-12 border-b border-slate-800"
        style={{
          backgroundImage: "url('/mods/eve2scarlet1.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Mod Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="secondary" className="bg-cyan-500/20 text-cyan-300 border-cyan-400/30">
                  Outfits
                </Badge>
                <Badge variant="secondary" className="bg-pink-500/20 text-pink-300 border-pink-400/30">
                  Character Transformation
                </Badge>
              </div>
              
              <h1 className="text-4xl font-bold mb-4">
                EVE2Scarlet
              </h1>
              
              <p className="text-xl opacity-90 mb-6">
                Transform EVE into Scarlet with complete appearance overhaul including face, hair, body and weapon modifications. Experience the game with a fresh visual perspective.
              </p>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Created by <a href="https://www.nexusmods.com/users/148748853" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">likefengzi</a></span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Updated: June 12, 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span>Version 1.0.0</span>
                </div>
              </div>
            </div>
            
            {/* Stats Card */}
            <div className="lg:col-span-1">
              <Card className="bg-slate-800/50 border-slate-700 text-white backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-cyan-300">
                    <BarChart3 className="h-5 w-5" />
                    Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Endorsements
                    </span>
                    <span className="font-bold">296</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Downloads
                    </span>
                    <span className="font-bold">4,523</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Views
                    </span>
                    <span className="font-bold">9,847</span>
                  </div>
                  <Separator className="bg-slate-600" />
                  <DownloadButton className="w-full bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700 text-white border-0 shadow-lg" scrollOnly>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </DownloadButton>
                  <p className="text-xs opacity-80 text-center">
                    Manual download available
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-cyan-300">About this mod</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none text-slate-300">
                <div className="space-y-6">
                  {/* Overview */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Overview:</h3>
                    <div className="bg-slate-800/30 border border-slate-600 p-4 rounded-lg backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        The EVE2Scarlet mod completely transforms EVE's appearance to match that of Scarlet, providing players with an entirely new visual experience. This comprehensive character modification includes facial features, hairstyle, body proportions, and weapon appearance changes.
                      </p>
                      <p className="text-sm mb-3">
                        Players can experience the familiar gameplay of Stellar Blade while enjoying Scarlet's distinctive appearance and aesthetic. The mod maintains all original game functionality while providing this visual transformation.
                      </p>
                      <p className="text-sm">
                        <strong>Important Note:</strong> This mod replaces EVE's Planet Diving Suit (Team 7) with Scarlet's appearance. Some elements may conflict with certain DLC content.
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Features */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Key Features:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <Palette className="h-4 w-4 mt-1 text-pink-600" />
                        <span><strong>Complete Face Transformation:</strong> Replaces EVE's facial features with Scarlet's distinctive appearance</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Image className="h-4 w-4 mt-1 text-purple-600" />
                        <span><strong>Hairstyle Change:</strong> Updates EVE's hairstyle to match Scarlet's iconic look</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <User className="h-4 w-4 mt-1 text-blue-600" />
                        <span><strong>Body Modification:</strong> Adjusts body proportions and appearance to match Scarlet</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Sword className="h-4 w-4 mt-1 text-red-600" />
                        <span><strong>Weapon Customization:</strong> Updates weapon appearance to complement the character transformation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-1 text-green-600" />
                        <span><strong>Full Compatibility:</strong> Works with all game modes and story progression</span>
                      </li>
                    </ul>
                  </div>

                  <Separator />

                  {/* Installation */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Installation Guide:</h3>
                    <ol className="space-y-2 list-decimal list-inside">
                      <li><strong>Download</strong> the EVE2Scarlet mod file</li>
                      <li><strong>Create a ~mods folder</strong> in your game's Paks directory: <code className="bg-slate-700 text-cyan-300 px-1 rounded">StellarBlade/SB/Content/Paks/~mods</code></li>
                      <li><strong>Extract</strong> all .pak files to the ~mods folder</li>
                      <li><strong>Important:</strong> Disable ponytail in game settings to avoid visual conflicts</li>
                      <li><strong>Launch the game</strong> and enjoy the transformation</li>
                    </ol>
                  </div>

                  <Separator />

                  {/* Important Notes */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Important Notes:</h3>
                    <div className="space-y-4">
                      <div className="bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-yellow-300">Ponytail Conflict</h4>
                        <p className="text-sm">
                          You must disable the ponytail setting in the game options menu. The ponytail conflicts with both the new hairstyle and weapon modifications, which may cause visual glitches.
                        </p>
                      </div>
                      
                      <div className="bg-blue-900/20 border-l-4 border-blue-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-blue-300">DLC Compatibility</h4>
                        <p className="text-sm">
                          If you experience issues with hair or body modifications, you can use the original DLC versions of these elements. The face and weapon modifications should work without conflicts.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Player Experience */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Player Experience:</h3>
                    <div className="bg-slate-800/30 border border-slate-600 p-4 rounded-lg backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        This visual transformation mod offers players a completely fresh perspective on Stellar Blade's gameplay. The attention to detail in recreating Scarlet's appearance is impressive, with carefully crafted facial features and proportions that maintain the character's recognizable aesthetic.
                      </p>
                      <p className="text-sm mb-3">
                        Players particularly appreciate how the mod maintains the game's visual quality while providing this dramatic character change. The weapon modifications complement the overall transformation, creating a cohesive visual experience.
                      </p>
                      <p className="text-sm">
                        The mod is especially popular among players who want to experience the game's story and combat from a different visual perspective, adding replay value to subsequent playthroughs.
                      </p>
                    </div>
                  </div>

                  {/* Support Links */}
                  <div className="flex flex-wrap gap-4 pt-6">
                    <DownloadButton 
                      variant="outline" 
                      className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-800"
                      scrollOnly
                    >
                      <Download className="h-4 w-4" />
                      Download Latest Version
                    </DownloadButton>
                    <Button variant="outline" asChild>
                      <a href="/contact" className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-800">
                        <MessageSquare className="h-4 w-4" />
                        Community Support
                      </a>
                    </Button>
                  </div>

                  <Separator />

                  {/* Original Page Link */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-pink-900/30 to-red-900/30 border border-slate-600 rounded-lg backdrop-blur-sm">
                    <h4 className="text-lg font-semibold mb-3 text-cyan-300">Visit Original Page</h4>
                    <p className="text-sm text-slate-300 mb-4">
                      Check the official Nexus Mods page for latest updates, community discussions, and additional screenshots.
                    </p>
                    <Button asChild className="bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700 text-white border-0 shadow-lg">
                      <a 
                        href="https://www.nexusmods.com/stellarblade/mods/210" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <Star className="h-4 w-4" />
                        View Nexus Mods Page
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* File Information */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">File Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-slate-300">
                  <div className="flex justify-between text-sm">
                    <span>Last updated</span>
                    <span>June 12, 2025</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Original upload</span>
                    <span>June 12, 2025</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Created by</span>
                    <a href="https://www.nexusmods.com/users/148748853" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">likefengzi</a>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Uploaded by</span>
                    <a href="https://www.nexusmods.com/users/148748853" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">likefengzi</a>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Virus scan</span>
                    <Badge variant="secondary" className="bg-green-900/50 text-green-300 border-green-600">
                      Safe to use
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Download Section */}
              <Card id="download-section" className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Download Files</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* File Information */}
                    <div className="p-4 border border-slate-600 rounded-lg">
                      <h4 className="font-semibold text-slate-200">EVE2Scarlet</h4>
                      <p className="text-sm text-slate-400">Complete character transformation</p>
                      <p className="text-xs text-slate-500">Version 1.0.0 • 85.6 MB</p>
                    </div>
                    
                    {/* Download Button */}
                    <div className="w-full">
                      <ProtectedDownloadLink 
                        href="https://r2.stellarblademod.com/EVE2Scarlet-210-1-0-0-1749707422.rar" 
                        className="w-full bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </ProtectedDownloadLink>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="border-slate-600 text-slate-300">character</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">transformation</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">scarlet</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">outfit</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">visual</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Permissions */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Permissions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-xs text-slate-400">
                    <p><strong className="text-slate-300">Upload permission:</strong> Not allowed</p>
                    <p><strong className="text-slate-300">Modification permission:</strong> Permission required</p>
                    <p><strong className="text-slate-300">Conversion permission:</strong> Not allowed</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 